import React, { useState, useEffect, useRef, useMemo } from "react";
import { 
  IconButton, 
  InputAdornment,
  TextField,
  Box
} from "@mui/material";
import { 
  ArrowBackIos, 
  ArrowForwardIos, 
  Add, 
  Remove, 
  Search
} from "@mui/icons-material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import "../OldUiComponents/OldMDMReportPreview.css";
import { newReportConfig } from "../mdmReportsUtils";
import { fetchReportData, fetchColumnDefinitions, FilterOption, ColumnOption } from "../../../services/reportsDataService";
import { getMergedFilterSources, isMergedFilterForReport } from "../../../services/mdmCustomFiltersService";
import dayjs from "dayjs";

interface MdmReportsPreviewProps {
  onBack?: () => void;
  newReportConfig: newReportConfig | null;
  filters: Record<string, string[]>;
  dateRange: {
    type: string;
    fromDate: string;
    toDate: string;
    selectedRange: string;
    period?: string;
    year?: string;
  };
  customFilters: FilterOption[];
}

const MdmReportsPreview: React.FC<MdmReportsPreviewProps> = ({ 
  onBack, 
  newReportConfig, 
  filters, 
  dateRange,
  customFilters
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [zoom, setZoom] = useState(100);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [reportData, setReportData] = useState<any[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [columnDefinitions, setColumnDefinitions] = useState<ColumnOption[]>([]);
  const [loading, setLoading] = useState(false);
  const pageSize = 30;
  const lastRequestIdRef = useRef(0);

  const filtersKey = useMemo(() => {
    return JSON.stringify(filters);
  }, [filters]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch]);

  useEffect(() => {
    setCurrentPage(1);
  }, [newReportConfig, filtersKey]);

  useEffect(() => {
    if (newReportConfig) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newReportConfig, filtersKey, debouncedSearch, currentPage, dateRange]);

  useEffect(() => {
    const loadReportMetadata = async () => {
      if (!newReportConfig?.reportName) {
        setColumnDefinitions([]);
        return;
      }
      
      try {
        const columns = await fetchColumnDefinitions(newReportConfig.reportName);
        setColumnDefinitions(columns);
      } catch (error) {
        setColumnDefinitions([]);
      }
    };
    
    loadReportMetadata();
  }, [newReportConfig?.reportName]);

  // Helper function to get source alias from merged filter
  const getSourceAliasFromMergedFilter = (mergedFilterAlias: string, value: string): string | undefined => {
    const mergedSources = getMergedFilterSources(mergedFilterAlias, newReportConfig);
    return mergedSources?.find(s => s.value === value)?.alias;
  };

  // Build dependency filters for inter-dependent custom filters
  const buildMergedFilterDependencies = (): Record<string, string[]> => {
    const dependencyFilters: Record<string, string[]> = {};

    const allMergedFilterSourceAliases = new Set<string>();
    if (newReportConfig?.mergedFilters) {
      Object.entries(newReportConfig.mergedFilters).forEach(([mergedFilterAlias, sources]) => {
        sources.forEach(source => {
          allMergedFilterSourceAliases.add(source.alias);
        });
      });
    }

    if (newReportConfig?.mergedFilters) {
      Object.entries(newReportConfig.mergedFilters).forEach(([mergedFilterAlias, sources]) => {
        const mergedFilterSelections = filters[mergedFilterAlias] || [];
        if (mergedFilterSelections.length > 0) {
          const selectedValue = mergedFilterSelections[0];
          const sourceAlias = getSourceAliasFromMergedFilter(mergedFilterAlias, selectedValue);
          
          if (sourceAlias) {
            const dynamicKey = `${mergedFilterAlias}_dynamic`;
            const dynamicSelections = filters[dynamicKey] || [];
            
            if (dynamicSelections.length > 0) {
              dependencyFilters[sourceAlias] = dynamicSelections;
            }
          }
        }
      });
    }

    customFilters.forEach(cf => {
      const vals = filters[cf.alias];
      if (vals && vals.length > 0 && vals[0] !== "") {
        dependencyFilters[cf.alias] = vals;
      }
    });

    return dependencyFilters;
  };

  const fetchData = async () => {
    if (!newReportConfig?.reportName) return;

    const requestId = ++lastRequestIdRef.current;
    
    const apiFilters: Record<string, string[]> = {};
    const levelKeys = [
      newReportConfig.salesHierarchyFilter?.levelFilterField,
      newReportConfig.geographicalHierarchyFilter?.levelFilterField
    ].filter(Boolean) as string[];
    
    const hierarchyOrder = newReportConfig.salesHierarchyFilter?.hierarchyOrder || [];
    const supplierLevel = hierarchyOrder[hierarchyOrder.length - 1] || 'supplier';
    
    const distributorFilterField = newReportConfig.distributorFilter?.field || 'distributor';

    const authContext = JSON.parse(localStorage.authContext || '{}');
    const loggedInUser = authContext?.user?.loginId;

    if(newReportConfig.isDistributorView && loggedInUser){
      apiFilters.distributor_code = [loggedInUser];
    }
    
    // Build inter dependency filters
    const dependencyFilters = buildMergedFilterDependencies();
    
    Object.entries(filters).forEach(([key, values]) => {
      if (levelKeys.includes(key)) {
        return;
      }
      
      if (key === supplierLevel || key === distributorFilterField) {
        if(values && values.length > 0 && values[0] !== ""){
          apiFilters.distributor_code = values;
        }
        return;
      }
      
      // Check if it's a custom filter (including merged filter dependencies)
      const isCustomFilter = customFilters.some(cf => cf.alias === key);
      const isMergedFilterKey = newReportConfig?.mergedFilters && key in newReportConfig.mergedFilters;
      const isDynamicKey = key.endsWith('_dynamic');
      
      if(isCustomFilter || isMergedFilterKey || isDynamicKey || dependencyFilters[key]){
        if(values && values.length > 0 && values[0] !== ""){
          // Use dependency filters for actual API call
          if (dependencyFilters[key]) {
            apiFilters[key] = dependencyFilters[key];
          } else if (!isMergedFilterKey && !isDynamicKey) {
            apiFilters[key] = values;
          }
        }
      }
    });

    if (!apiFilters.distributor_code || apiFilters.distributor_code.length === 0) {
      if (requestId === lastRequestIdRef.current) {
        setReportData([]);
        setHasMore(false);
        setLoading(false);
      }
      return;
    }

    setLoading(true);
    
    try {
      const commaSeparatedFilters = Array.from(
        new Set([
          'distributor_code',
          ...customFilters.map(cf => cf.alias),
          ...Object.keys(dependencyFilters)
        ])
      );

      const requestParams: any = {
        getAPI: newReportConfig.getAPI,
        report: newReportConfig.reportName,
        page: currentPage,
        pageSize: pageSize,
        contains: debouncedSearch || undefined,
        filters: Object.keys(apiFilters).length > 0 ? apiFilters : undefined,
        commaSeparatedFilters
      };

      if (dateRange.type === "period" && dateRange.period && dateRange.year) {
        requestParams.period = dateRange.period;
        requestParams.year = dateRange.year;
      } else if (dateRange.fromDate && dateRange.toDate) {
        const fromDateDayjs = dayjs(dateRange.fromDate, "DD-MM-YYYY");
        const toDateDayjs = dayjs(dateRange.toDate, "DD-MM-YYYY");
        
        const fromDateISO = new Date(Date.UTC(
          fromDateDayjs.year(),
          fromDateDayjs.month(),
          fromDateDayjs.date() - 1,
          18,
          30,
          0
        )).toISOString().replace(/\.\d{3}Z$/, 'Z');
        
        const toDateISO = new Date(Date.UTC(
          toDateDayjs.year(),
          toDateDayjs.month(),
          toDateDayjs.date(),
          18,
          29,
          59
        )).toISOString().replace(/\.\d{3}Z$/, 'Z');
        
        if(newReportConfig.dateRangeFilter){
          requestParams.since = fromDateISO;
          requestParams.until = toDateISO;
        }
      }
      
      

      const response = await fetchReportData(requestParams);

      if (requestId === lastRequestIdRef.current) {
        const items = response.items || [];
        setReportData(items);
        setHasMore(items.length === pageSize); 
      }
    } catch (error) {
      if (requestId === lastRequestIdRef.current) {
        setReportData([]);
        setHasMore(false);
      }
    } finally {
      if (requestId === lastRequestIdRef.current) {
        setLoading(false);
      }
    }
  };

  const generateColumnsFromData = (data: any[]): GridColDef[] => {
    if (!data || data.length === 0) {
      return [];
    }
    const columnsToHide = newReportConfig?.columnsToHide || [];
    
    const dataKeys = Object.keys(data[0]).filter(k => k !== '@filters' && !columnsToHide.includes(k));
    
    if (columnDefinitions.length > 0) {
      const orderedColumns = columnDefinitions
        .filter(colDef => dataKeys.includes(colDef.alias) && !columnsToHide.includes(colDef.alias))
        .map(colDef => ({
          field: colDef.alias,
          headerName: colDef.display,
          minWidth: 150,
          flex: 1,
          sortable: false,
        }));
      return orderedColumns;
    }
    return [];
  };

  const rowsToShow = reportData || [];

  const effectiveColumns: GridColDef[] = useMemo(() => {
    return generateColumnsFromData(rowsToShow);
  }, [rowsToShow, columnDefinitions, newReportConfig?.columnsToHide]);

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const handleNextPage = () => {
    if (hasMore) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const handleZoomIn = () => {
    if (zoom < 150) {
      setZoom(zoom + 10);
    }
  };

  const handleZoomOut = () => {
    if (zoom > 50) {
      setZoom(zoom - 10);
    }
  };

  return (
    <div className="mdm-preview-container">
      {/* Controls Header */}
      <div className="mdm-preview-controls">
        <div className="mdm-preview-controls-left">
          <IconButton 
            size="small" 
            onClick={handlePreviousPage}
            disabled={currentPage === 1}
            className="mdm-preview-nav-btn"
          >
            <ArrowBackIos fontSize="small" />
          </IconButton>
          <span className="mdm-preview-page-info">
            Page {currentPage}
          </span>
          <IconButton 
            size="small" 
            onClick={handleNextPage}
            disabled={!hasMore || loading}
            className="mdm-preview-nav-btn"
          >
            <ArrowForwardIos fontSize="small" />
          </IconButton>
          <div className="mdm-preview-divider" />
          <IconButton size="small" onClick={handleZoomIn} className="mdm-preview-zoom-btn">
            <Add fontSize="small" />
          </IconButton>
          <span className="mdm-preview-zoom-level">{zoom}%</span>
          <IconButton size="small" onClick={handleZoomOut} className="mdm-preview-zoom-btn">
            <Remove fontSize="small" />
          </IconButton>
        </div>
        <div className="mdm-preview-controls-right">
          <TextField
            size="small"
            placeholder="Search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
            className="mdm-preview-search"
          />
        </div>
      </div>

      {/* DataGrid Container */}
      <Box 
        className="mdm-preview-table-container" 
        sx={{ 
          height: 'calc(100% - 150px)',
          width: '100%',
          transform: `scale(${zoom / 100})`,
          transformOrigin: 'top left',
          '& .MuiDataGrid-root': {
            border: 'none',
          },
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: '#FFF9C4',
            fontWeight: 600,
            fontSize: '13px'
          },
          '& .MuiDataGrid-cell': {
            fontSize: '12px',
            borderRight: '1px solid #e0e0e0'
          },
          '& .MuiDataGrid-row:nth-of-type(even)': {
            backgroundColor: '#f9f9f9'
          }
        }}
      >
        <DataGrid
          rows={rowsToShow}
          columns={effectiveColumns}
          getRowId={(row) => row.id || row._id || Math.random().toString()}
          pageSizeOptions={[pageSize]}
          disableRowSelectionOnClick
          disableColumnMenu
          hideFooter
          loading={loading}
          sx={{
            '& .MuiDataGrid-virtualScroller': {
              minHeight: '400px'
            }
          }}
        />
      </Box>

    </div>
  );
};

export default MdmReportsPreview;

